/**
 * Image validation and processing utilities
 */

export interface ImageValidationResult {
  valid: boolean;
  error?: string;
  image?: HTMLImageElement;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

// Configuration
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MIN_DIMENSIONS = { width: 200, height: 200 };
const RECOMMENDED_DIMENSIONS = { width: 500, height: 500 };
const ACCEPTED_FORMATS = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Validates image file for proper dimensions, size and format
 */
export const validateImageFile = (
  file: File
): Promise<ImageValidationResult> => {
  return new Promise((resolve) => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      resolve({
        valid: false,
        error: `File is too large. Maximum size is ${
          MAX_FILE_SIZE / (1024 * 1024)
        }MB.`,
      });
      return;
    }

    // Check file type
    if (!ACCEPTED_FORMATS.includes(file.type)) {
      resolve({
        valid: false,
        error: `Invalid file format. Accepted formats: JPEG, PNG, GIF, WEBP.`,
      });
      return;
    }

    // Check image dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      if (
        img.width < MIN_DIMENSIONS.width ||
        img.height < MIN_DIMENSIONS.height
      ) {
        resolve({
          valid: false,
          error: `Image is too small. Minimum dimensions: ${MIN_DIMENSIONS.width}x${MIN_DIMENSIONS.height}px.`,
        });
        return;
      }

      resolve({
        valid: true,
        image: img,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        valid: false,
        error: "Failed to load image. The file may be corrupted.",
      });
    };

    img.src = objectUrl;
  });
};

/**
 * Processes an image file without cropping (preserves original image shape)
 * @param file Original image file
 * @param quality JPEG quality (0-1)
 */
export const createCircularAvatar = async (
  file: File,
  cropScale: number = 1.0,
  quality: number = 0.9
): Promise<Blob | null> => {
  try {
    // Validate file first
    const validation = await validateImageFile(file);
    if (!validation.valid || !validation.image) {
      console.error("Image validation failed:", validation.error);
      return null;
    }

    const img = validation.image;

    // Create canvas for image processing (no cropping)
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.error("Could not get canvas context");
      return null;
    }

    // Draw original image without any cropping
    ctx.drawImage(img, 0, 0, img.width, img.height);

    // Convert to blob with compression
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
    });
  } catch (error) {
    console.error("Error processing avatar image:", error);
    return null;
  }
};

/**
 * Creates a preview data URL for an image file
 */
export const createImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Checks if an image is likely too pixelated based on dimensions
 */
export const isLikelyPixelated = (image: HTMLImageElement): boolean => {
  return (
    image.width < RECOMMENDED_DIMENSIONS.width ||
    image.height < RECOMMENDED_DIMENSIONS.height
  );
};

/**
 * Provides user-friendly feedback based on image analysis
 */
export const getImageQualityFeedback = (
  image: HTMLImageElement
): string | null => {
  if (isLikelyPixelated(image)) {
    return "This image may appear pixelated. For best results, use images at least 500x500 pixels.";
  }

  const aspectRatio = image.width / image.height;
  if (aspectRatio < 0.75 || aspectRatio > 1.33) {
    return "This image has an unusual aspect ratio. Square images work best for profile pictures.";
  }

  return null;
};

/**
 * Ensures an image URL is suitable for social media sharing
 * Filters out data URLs and blob URLs which won't work on social media
 * @param url The image URL to validate
 * @returns The URL if valid for social media, null otherwise
 */
export const getSocialMediaSafeImageUrl = (
  url: string | null | undefined
): string | null => {
  if (!url) {
    return null;
  }

  // Data URLs and blob URLs won't work for social media sharing
  if (url.startsWith("data:") || url.startsWith("blob:")) {
    return null;
  }

  // Handle Supabase URLs - ensure they're properly formatted
  if (url.includes("supabase.co")) {
    // Ensure Supabase URLs are HTTPS
    if (url.startsWith("http://")) {
      url = url.replace("http://", "https://");
    }

    // Check if it's a public Supabase storage URL
    if (url.includes("/storage/v1/object/public/")) {
      return url;
    }

    // If it's a relative Supabase URL, it should be fine
    if (url.startsWith("/")) {
      return url;
    }

    // If it's not a public storage URL, it might be private
    return null;
  }

  // Handle other external URLs
  if (url.startsWith("http")) {
    return url;
  }

  // Handle relative URLs
  if (url.startsWith("/")) {
    return url;
  }

  // If it's not a valid URL format, reject it
  try {
    new URL(url);
    return url;
  } catch (e) {
    return null;
  }
};

export const convertSupabaseUrlToRenderUrl = (
  url: string | null | undefined,
  quality: number = 50
): string | null => {
  // Handle null/undefined
  if (!url) {
    return null;
  }

  // Keep data URLs as-is
  if (url.startsWith("data:")) {
    return url;
  }

  // If it's an external URL (not Supabase), return as-is
  if (url.startsWith("http") && !url.includes("supabase.co")) {
    return url;
  }

  // Check if URL contains /public/ before splitting
  if (!url.includes("/public/")) {
    // If it's a Supabase URL but missing /public/, something's wrong
    if (url.includes("supabase.co")) {
      console.warn("Supabase URL missing /public/ segment:", url);
      return url; // Return as-is rather than break
    }
    // Not a Supabase URL, return as-is
    return url;
  }

  const publicPath = url.split("/public/")[1];

  // Double-check publicPath exists
  if (!publicPath) {
    console.warn("Failed to extract public path from:", url);
    return url;
  }

  if (url.endsWith(".gif")) {
    return `https://worldclassdesign.b-cdn.net/${publicPath}?quality=${quality}&format=gif`;
  }
  return `https://worldclassdesign.b-cdn.net/${publicPath}?quality=${quality}&format=webp`;
};

export const convertSupabaseUrlToBase64 = (url: string): string => {
  // convert the url to base64
  const base64Url = btoa(url);
  return base64Url;
};
